import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Helper function using XMLHttpRequest to avoid "body disturbed" issues
const apiRequest = (method, url, data = null) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        try {
          const response = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(response);
          } else {
            reject(new Error(response.detail || 'Request failed'));
          }
        } catch (e) {
          reject(new Error('Błąd serwera. Spróbuj ponownie.'));
        }
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Błąd połączenia z serwerem'));
    };
    
    if (data) {
      xhr.send(JSON.stringify(data));
    } else {
      xhr.send();
    }
  });
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API}/auth/me`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              const userData = JSON.parse(xhr.responseText);
              setUser(userData);
            } catch (e) {
              setUser(null);
              localStorage.removeItem('auth_token');
              setToken(null);
            }
          } else {
            setUser(null);
            localStorage.removeItem('auth_token');
            setToken(null);
          }
          setLoading(false);
        }
      };
      
      xhr.onerror = function() {
        setUser(null);
        setLoading(false);
      };
      
      xhr.send();
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const data = await apiRequest('POST', `${API}/auth/login`, { email, password });
    
    localStorage.setItem('auth_token', data.access_token);
    setToken(data.access_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, userType, consents = {}) => {
    const data = await apiRequest('POST', `${API}/auth/register`, {
      email, 
      password, 
      name, 
      user_type: userType,
      terms_accepted: consents.terms_accepted || false,
      privacy_accepted: consents.privacy_accepted || false,
      marketing_accepted: consents.marketing_accepted || false,
    });
    
    localStorage.setItem('auth_token', data.access_token);
    setToken(data.access_token);
    setUser(data);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard/individual';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const userData = await apiRequest('POST', `${API}/auth/google-session`, { session_id: sessionId });
    
    if (userData.access_token) {
      localStorage.setItem('auth_token', userData.access_token);
      setToken(userData.access_token);
    }
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/logout`, true);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send();
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const updateUserPoints = (newBalance) => {
    if (user) {
      setUser({ ...user, points_balance: newBalance });
    }
  };

  const value = {
    user,
    loading,
    token,
    login,
    register,
    loginWithGoogle,
    processGoogleSession,
    logout,
    checkAuth,
    updateUserPoints,
    isAuthenticated: !!user,
    isCompany: user?.user_type === 'company',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
